import Cookies from 'js-cookie';
import apiService from './apiService';
import { AxiosResponse } from 'axios';
import { toast } from 'react-toastify';

const api = apiService();

/* ---------- response interfaces ---------- */
interface RegisterResponse {
  success: boolean;
  message?: string;
  gmail_link?: string;
}

interface LoginResponse {
  success: boolean;
  message?: string;
  user?: {
    id: string;
    name: string;
    email: string;
    phone: string;
    date_of_birth: string;
    profile_pic: string | null;
  };
}

interface ForgotPasswordResponse {
  success: boolean;
  message?: string;
}

interface VerifyOTPResponse {
  success: boolean;
  message?: string;
  email?: string;
}

interface UserData {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  date_of_birth: string | null;
  profile_pic: string | null;
  gender: string | null;
  followers: string;
  following: string;
}

type RoleName = 'admin' | 'user' | 'publisher' | 'moderator' | string;

interface Role {
  name: RoleName;
}

interface Permission {
  id: string;
  key: string;
}

interface Pagination {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
}

interface ProfileResponse {
  status: number;
  message?: string;
  data: UserData[]; // array (server returns [ { ... } ])
  pagination?: Pagination; // optional
  role?: Role[];
  permission?: Permission[];
}

interface GoogleLoginResponse {
  success: boolean;
  message?: string;
}
interface LogoutResponse {
  success: boolean;
}
interface AuthCheckResponse {
  success: boolean;
}
interface ServiceResult {
  success: boolean;
  message?: string;
  verifyLink?: string;
}

/* ---------- small utils ---------- */
const COOKIE_OPTS = { expires: 7, path: '/', sameSite: 'lax' as const };

function safeParse<T>(val: string | undefined, fallback: T): T {
  if (!val) return fallback;
  try {
    return JSON.parse(val) as T;
  } catch {
    return fallback;
  }
}

/* ---------- main class ---------- */
class AuthService {
  /* ----- sign-up -------------------------------------------------------- */
  static async register(
    name: string,
    email: string,
    phone: string,
    dateOfBirth: string,
    password: string,
    gender: string,
  ): Promise<ServiceResult> {
    try {
      // Pull stashed UTM source (set in src/index.tsx at app boot from the
      // landing URL) and forward to backend so users.referrer_source is
      // populated at signup. Null when the user arrived without UTMs.
      const { buildReferrerSource } = await import('../utils/attribution');
      const referrerSource = buildReferrerSource();
      const res: AxiosResponse<RegisterResponse> = await api.post(
        '/auth/signup',
        {
          name,
          email,
          phone,
          dateOfBirth,
          password,
          gender,
          referrerSource,
        },
      );
      toast.success(res.data.message ?? 'Registered successfully');
      return {
        success: res.data.success,
        message: res.data.message,
        verifyLink: res.data.gmail_link,
      };
    } catch {
      toast.error('Registration failed');
      return { success: false, message: 'Registration failed' };
    }
  }

  /* ----- invite --------------------------------------------------------- */
  static async invite(
    email: string,
    role: string[],
    permissions: string[] = [],
  ): Promise<ServiceResult> {
    try {
      const res: AxiosResponse<RegisterResponse> = await api.post(
        '/auth/invite',
        {
          email,
          role,
          permissions,
        },
      );
      toast.success(res.data.message ?? 'Send Invite Successfully');
      return { success: res.data.success, message: res.data.message };
    } catch {
      toast.error('Invite failed');
      return { success: false, message: 'Invite failed' };
    }
  }

  /* ----- persist helpers ------------------------------------------------ */
  static saveUserData(profile: ProfileResponse) {
    // ✅ Cookies: roles + permissions (stringified)
    Cookies.set('user_roles', JSON.stringify(profile.role ?? []), COOKIE_OPTS);
    Cookies.set(
      'user_permissions',
      JSON.stringify(profile.permission ?? []),
      COOKIE_OPTS,
    );

    // Optional: keep a friendly name cookie for quick header use
    const firstUser = profile.data?.[0];
    if (firstUser?.name) {
      Cookies.set('user_name', firstUser.name, COOKIE_OPTS);
    }

    // ✅ LocalStorage: rest of the data except roles/permissions
    const { role, permission, ...rest } = profile;
    localStorage.setItem('user_profile', JSON.stringify(rest));
  }

  /* ----- verifyOTP  --------------------------------------- */
  static async verifyOTP(email: string, otp: string): Promise<VerifyOTPResponse> {
    try {
      // 1) Login
      const res: AxiosResponse<VerifyOTPResponse> = await api.post(
        '/auth/verify-otp',
        { email, otp },
      );

      if (res.data.success) {
        toast.success(res.data.message ?? 'OTP verified successfully');
        return { success: true };
      }

      toast.error(res.data.message ?? 'Invalid OTP');
      return { success: false, message: res.data.message };
    } catch (err: any) {
      toast.error(err?.response.data.message ?? 'Invalid OTP');
      return { success: false, message: 'Invalid OTP' };
    }
  }

  /* ----- resetPassword  --------------------------------------- */
  static async resetPassword(email: string, otp: string, password: string): Promise<ServiceResult> {
    try {
      // 1) Login
      const res: AxiosResponse<VerifyOTPResponse> = await api.post(
        '/auth/reset-password',
        { email, otp, password },
      );
      

      console.log(res);
      

      if (res.data.success) {
        toast.success(res.data.message ?? 'Password reset successfully go to login');
        return { success: true };
      }

      toast.error(res.data.message ?? 'Invalid OTP');
      return { success: false, message: res.data.message };
    } catch (err: any) {
      toast.error(err?.response.data.message ?? 'Invalid OTP');
      return { success: false, message: 'Invalid OTP' };
    }
  }

  /* ----- Forgot password  --------------------------------------- */
  static async forgotPassword(email: string): Promise<ServiceResult> {
    try {
      // 1) Login
      const res: AxiosResponse<ForgotPasswordResponse> = await api.post(
        '/auth/forgot-password',
        { email },
      );

      if (res.data.success) {
        toast.success(res.data.message ?? 'Otp Sent Successfully');
        return { success: true };
      }

      toast.error(res.data.message ?? 'Email Wrong');
      return { success: false, message: res.data.message };
    } catch (err: any) {
      toast.error(err?.response.data.message ?? 'Email Wrong');
      return { success: false, message: 'Email Wrong' };
    }
  }

  /* ----- e-mail / password login --------------------------------------- */
  static async login(email: string, password: string): Promise<ServiceResult> {
    try {
      // 1) Login
      const loginRes: AxiosResponse<LoginResponse> = await api.post(
        '/auth/login',
        { email, password },
      );

      if (loginRes.data.success && loginRes.data.user) {
        // 2) Profile fetch
        const profileRes: AxiosResponse<ProfileResponse> =
          await api.get('/user');
        // 3) Persist
        this.saveUserData(profileRes.data);

        toast.success('Logged in successfully');
        return { success: true };
      }

      toast.error(loginRes.data.message ?? 'Login failed');
      return { success: false, message: loginRes.data.message };
    } catch (err: any) {
      toast.error(err?.response.data.message ?? 'Login failed');
      return { success: false, message: 'Login failed' };
    }
  }

  /* ----- Google OAuth login -------------------------------------------- */
  static async googleLogin(code: string): Promise<GoogleLoginResponse> {
    try {
      const res = await api.post('/auth/login-google', {code: code});
      if (res.data.success && res.data.user) {
        // 2) Profile fetch
        const profileRes: AxiosResponse<ProfileResponse> =
          await api.get('/user');
        // 3) Persist
        this.saveUserData(profileRes.data);

        toast.success('Logged in successfully');
        return { success: true };
      }
      toast.error(res.data.message ?? 'Google Login failed');
      return { success: false, message: res.data.message };
    } catch (err: any) {
      toast.error(err?.response.data.message ?? 'Google Login failed');
      return { success: false, message: err?.message ?? 'Google login failed' };
    }
  }

  /* ----- facebook OAuth login -------------------------------------------- */
  static async facebookLogin(code: string): Promise<GoogleLoginResponse> {
    try {
      const res = await api.post('/auth/login-facebook', {code: code});
      if (res.data.success && res.data.user) {
        // 2) Profile fetch
        const profileRes: AxiosResponse<ProfileResponse> =
          await api.get('/user');
        // 3) Persist
        this.saveUserData(profileRes.data);

        toast.success('Logged in successfully');
        return { success: true };
      }
      toast.error(res.data.message ?? 'Google Login failed');
      return { success: false, message: res.data.message };
    } catch (err: any) {
      toast.error(err?.response.data.message ?? 'Google Login failed');
      return { success: false, message: err?.message ?? 'Google login failed' };
    }
  }

  /* ----- logout --------------------------------------------------------- */
  static async logout(): Promise<LogoutResponse> {
    try {
      await api.post('/auth/logout');
    } catch {
      // ignore API error; still clear client state
    } finally {
      Cookies.remove('user_roles');
      Cookies.remove('user_permissions');
      Cookies.remove('user_name');
      localStorage.removeItem('user_profile');
      toast.success('Logged out successfully');
    }
    return { success: true };
  }

  /* ----- server-side auth check ---------------------------------------- */
static async checkAuthAdmin(): Promise<boolean> {
  try {
    const res = await api.get<ProfileResponse>('/user');

    // ✅ Save profile (same as login me hota tha)
    this.saveUserData(res.data);

    // ✅ Role check
    const roles = this.getUserRoles(); // from cookies
    const isAllowed = roles.includes('admin') || roles.includes('moderator');

    if (!isAllowed) {
      toast.error('Access denied: Admin/Moderator only');
      await this.logout();
      window.location.href = '/login';
      return false;
    }

    return true;
  } catch (err: any) {
    const status = err?.response?.status;

    if (status === 401) {
      // toast.info('Session expired, refreshing token…');
      return await this.refreshToken();
    }
    if (status === 403) {
      await this.logout();
      return false;
    }

    console.error('Auth check error:', err);
    toast.error(err?.message ?? 'Authentication check failed');
    return false;
  }
}


static async checkAuthPublisher(): Promise<boolean> {
  try {
    const res = await api.get<ProfileResponse>('/user');

    // ✅ Save profile (roles, permissions, profile info)
    this.saveUserData(res.data);

    // ✅ Role check
    const roles = this.getUserRoles(); // ['user', 'publisher', ...]
    const isAllowed = roles.includes('user') || roles.includes('publisher');

    if (!isAllowed) {
      toast.error('Access denied: Publisher/User only');
      await this.logout();
      window.location.href = '/login';
      return false;
    }

    return true;
  } catch (err: any) {
    const status = err?.response?.status;

    if (status === 401) {
      // toast.info('Session expired, refreshing token…');
      return await this.refreshToken();
    }
    if (status === 403) {
      await this.logout();
      return false;
    }

    console.error('Auth check error:', err);
    toast.error(err?.message ?? 'Authentication check failed');
    return false;
  }
}


  static async refreshToken(): Promise<boolean> {
    try {
      const res = await api.post<AuthCheckResponse>('/auth/refresh-token');
      if (res.status === 200) {
        // toast.success('Token refreshed successfully');
        return true;
      }
      return false;
    } catch {
      await this.logout();
      return false;
    }
  }

  /* ----- getters / helpers --------------------------------------------- */
  static isLoggedIn(): boolean {
    // role cookie present => user persisted (tokens are httpOnly server-side)
    return !!Cookies.get('user_roles');
  }

  static getUserName(): string | null {
    return Cookies.get('user_name') ?? null;
  }

  static getUserRoles(): RoleName[] {
    const roles = safeParse<Role[]>(Cookies.get('user_roles'), []);
    return roles.map((r) => r.name);
  }

  static getUserPermissions(): string[] {
    const perms = safeParse<Permission[]>(Cookies.get('user_permissions'), []);
    return perms.map((p) => p.key);
  }

  static getUserProfile(): {
    status: number;
    message?: string;
    data: UserData[];
    pagination?: Pagination;
  } | null {
    const raw = localStorage.getItem('user_profile');
    return raw ? safeParse(raw, null) : null;
  }

  static hasPermission(key: string): boolean {
    const perms = this.getUserPermissions(); // ['Gamic','Campaign','User']
    // Admin sab kar sakta hai
    if (this.getUserRoles().includes('admin')) return true;
    return perms.includes(key);
  }

  static getRedirect(): string {
    const roles = this.getUserRoles();
    if (roles.includes('admin') || roles.includes('moderator')) {
      return '/admin/dashboard';
    }
    if (roles.includes('user') || roles.includes('publisher')) {
      return '/publisher/dashboard';
    }
    return '/login';
  }
}

export default AuthService;
